// @flow

import * as R from 'ramda';

import { HttpService } from '~/services';
import {
  getRouteNavigationParamsWithFormattedDate,
  renameKeys,
} from '~/helpers';

export const resolvers = {
  Query: {
    archivedSuperOrderPassiveIncome: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?SuperOrderPassiveIncomeResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        VaultCapacity: 'vaultCapacity',
        SopIncome: 'sopIncome',
        NetIncome: 'netIncome',
        VaultExcess: 'vaultExcess',
        LockedEarnings: 'lockedEarnings',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          vaultCapacity: ['VaultCapacityFrom', 'VaultCapacityTo'],
          sopIncome: ['SopIncomeFrom', 'SopIncomeTo'],
          netIncome: ['NetIncomeFrom', 'NetIncomeTo'],
          vaultExcess: ['VaultExcessFrom', 'VaultExcessTo'],
          lockedEarnings: ['LockedEarningsFrom', 'LockedEarningsTo'],
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get self consumption list
      const {
        data: { SopIncomeList, PageCount = 1 },
      } = await HttpService.client.get(
        '/Legacy/v2/MyWealth/DepositReward/GetSopIncomeTrxList',
        { params },
      );

      const items: SuperOrderPassiveIncomeItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            vaultCapacity: parseFloat,
            sopIncome: parseFloat,
            netIncome: parseFloat,
            vaultExcess: parseFloat,
            lockedEarnings: parseFloat,
          }),
          R.assoc('__typename', 'SuperOrderPassiveIncomeItemType'),
        ),
        SopIncomeList,
      );

      const superOrderPassiveIncome = {
        data: items,
        pages: PageCount,
        __typename: 'SuperOrderPassiveIncomeListType',
      };

      cache.writeData({
        data: { superOrderPassiveIncome },
        variables: props,
      });

      return superOrderPassiveIncome;
    },
    citizenReferralCommissionsHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?ArchivedCitizenReferralCommissionsHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        PlatformName: 'platformName',
        MerchantReferralComm: 'merchantReferralCommission',
        ReferralComm: 'citizenReferralCommission',
        TotalComm: 'totalCommission',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          platformName: 'PlatformName',
          merchantReferralCommission: [
            'MerchantReferralCommissionFrom',
            'MerchantReferralCommissionTo',
          ],
          citizenReferralCommission: ['ReferralCommFrom', 'ReferralCommTo'],
          totalCommission: ['TotalCommFrom', 'TotalCommTo'],
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get self consumption list
      const {
        data: { ReferralCommList, PageCount = 1 },
      } = await HttpService.client.get(
        '/Legacy/v2/MyWealth/Consumption/GetReferralCommTrxList',
        { params },
      );

      const items: ArchivedCitizenReferralCommissionsHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            merchantReferralCommission: parseFloat,
            citizenReferralCommission: parseFloat,
            totalCommission: parseFloat,
          }),
          R.assoc(
            '__typename',
            'ArchivedCitizenReferralCommissionsHistoryItem',
          ),
        ),
        ReferralCommList,
      );

      const citizenReferralCommissionsHistory = {
        data: items,
        pages: PageCount,
        __typename: 'ArchivedCitizenReferralCommissionsHistory',
      };

      cache.writeData({
        data: { citizenReferralCommissionsHistory },
        variables: props,
      });

      return citizenReferralCommissionsHistory;
    },
    archivedSuperOrderCommissionsHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?ArchivedSuperOrderCommissionsHistoryResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        SuperOrderComm: 'archivedSuperOrderCommission',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          archivedSuperOrderCommission: [
            'SuperOrderCommFrom',
            'SuperOrderCommTo',
          ],
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get self consumption list
      const {
        data: { SuperOrderCommList, PageCount = 1 },
      } = await HttpService.client.get(
        '/Legacy/v2/MyWealth/Consumption/GetSuperOrderCommTrxList',
        { params },
      );

      const items: ArchivedSuperOrderCommissionsHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            archivedSuperOrderCommission: parseFloat,
          }),
          R.assoc('__typename', 'ArchivedSuperOrderCommissionsHistoryItem'),
        ),
        SuperOrderCommList,
      );

      const archivedSuperOrderCommissionsHistory = {
        data: items,
        pages: PageCount,
        __typename: 'ArchivedSuperOrderCommissionsHistory',
      };

      cache.writeData({
        data: { archivedSuperOrderCommissionsHistory },
        variables: props,
      });

      return archivedSuperOrderCommissionsHistory;
    },
  },
};
